import { lazy, Suspense, useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import ProtectedRoute from '@/components/auth/ProtectedRoute'
import ErrorBoundary from '@/components/ErrorBoundary'
import MobileNavigation from '@/components/layout/MobileNavigation'
import { OfflineBadge } from '@/components/layout/OfflineIndicator'
import { CelebrationProvider } from '@/components/providers/CelebrationProvider'
import InstallPrompt from '@/components/pwa/InstallPrompt'
import { useGameCelebrations } from '@/hooks/useGameCelebrations'
import { useGameStore } from '@/lib/store/gameStore'
import HomePage from '@/pages/HomePage'
import LandingPage from '@/pages/LandingPage'
import type { User } from '@/providers'
import { getAuthService } from '@/services/AuthService'

const Wardrobe = lazy(() => import('@/pages/Wardrobe'))
const Scanner = lazy(() => import('@/pages/Scanner'))
const OutfitBuilder = lazy(() => import('@/pages/OutfitBuilder'))
const Recommendations = lazy(() => import('@/pages/Recommendations'))
const Progress = lazy(() => import('@/pages/Progress'))
const Education = lazy(() => import('@/pages/Education'))
const Challenges = lazy(() => import('@/pages/Challenges'))
const Profile = lazy(() => import('@/pages/Profile'))
const Auth = lazy(() => import('@/pages/Auth'))
const Onboarding = lazy(() => import('@/pages/Onboarding'))
const Insights = lazy(() => import('@/pages/Insights'))
const Avatar = lazy(() => import('@/pages/Avatar'))
const ColorProfile = lazy(() => import('@/pages/ColorProfile'))
const Shop = lazy(() => import('@/pages/Shop'))
const Subscription = lazy(() => import('@/pages/Subscription'))
const Leaderboard = lazy(() => import('@/pages/Leaderboard'))
const AICoach = lazy(() => import('@/pages/AICoach'))

function HomeRoute() {
  const [user, setUser] = useState<User | null | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const onboardingCompleted = useGameStore((s) => s.onboarding.completed)

  useEffect(() => {
    const authService = getAuthService()
    authService.getCurrentUser().then((user) => {
      setUser(user)
      setLoading(false)
    })

    const unsubscribe = authService.onAuthStateChange((user) => {
      setUser(user)
    })

    return () => unsubscribe()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-surface-elevated rounded-lg shadow-md p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      </div>
    )
  }

  if (user && !onboardingCompleted) {
    return <Navigate to="/onboarding" replace />
  }

  return user ? <HomePage /> : <LandingPage />
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="bg-surface-elevated rounded-lg shadow-md p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-sm text-fg-tertiary">Loading...</p>
      </div>
    </div>
  )
}

function GameCelebrationWatcher() {
  useGameCelebrations()
  return null
}

export default function App() {
  return (
    <ErrorBoundary>
      <CelebrationProvider>
        <GameCelebrationWatcher />
        <div className="fixed top-3 right-3 z-50">
          <OfflineBadge />
        </div>
        <div className="flex min-h-full flex-col bg-bg text-fg-primary transition-colors duration-300">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/education" element={<Education />} />
              <Route path="/challenges" element={<Challenges />} />

              {/* Home - shows landing for anonymous, dashboard for authenticated */}
              <Route path="/" element={<HomeRoute />} />

              {/* Onboarding */}
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <Onboarding />
                  </ProtectedRoute>
                }
              />

              {/* Wardrobe section */}
              <Route
                path="/wardrobe"
                element={
                  <ProtectedRoute>
                    <Wardrobe />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/scanner"
                element={
                  <ProtectedRoute>
                    <Scanner />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/outfit-builder"
                element={
                  <ProtectedRoute>
                    <OutfitBuilder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/recommendations"
                element={
                  <ProtectedRoute>
                    <Recommendations />
                  </ProtectedRoute>
                }
              />

              {/* Profile section */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/progress"
                element={
                  <ProtectedRoute>
                    <Progress />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/insights"
                element={
                  <ProtectedRoute>
                    <Insights />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/avatar"
                element={
                  <ProtectedRoute>
                    <Avatar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/color-profile"
                element={
                  <ProtectedRoute>
                    <ColorProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <Subscription />
                  </ProtectedRoute>
                }
              />

              {/* Social & extras */}
              <Route
                path="/shop"
                element={
                  <ProtectedRoute>
                    <Shop />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leaderboard"
                element={
                  <ProtectedRoute>
                    <Leaderboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-coach"
                element={
                  <ProtectedRoute>
                    <AICoach />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
          <InstallPrompt />
          <MobileNavigation />
        </div>
      </CelebrationProvider>
    </ErrorBoundary>
  )
}
